import { useState, useRef, useEffect } from "react";
import { lazy, Suspense } from 'react';
import { Auth } from "./services/authService";
import { changeEnterprise, changeEnterpriseDisplayName, changeIsSuperAdmin, changeLoginEmail, changeModulePermissions } from "./redux/action";
import { canRead, SCREEN_TO_MODULE, getFirstAvailableScreen } from "./utils/permissions";
import { useDispatch, useSelector } from "react-redux";
import "./styling/marketing.css";

const OutputPortfolio = lazy(() => import("./components/Home/OutputPortfolio"));
const OutputIndividual = lazy(() => import("./components/CropSuitability/OutputIndividual"));
const CalculateRiskMain = lazy(() => import("./components/CropSuitability/CalculateRiskMain"));
const Monitor = lazy(() => import("./components/Monitor/Monitor"));
const Advisory = lazy(() => import("./components/Advisory/Advisory"));
const LandingPage = lazy(() => import("./components/LandingPage/LandingPage"));
const AdminPage = lazy(() => import("./components/AdminProfile/AdminPage"));
const GuidedAdvisorDrawer = lazy(() => import("./components/GuidedAdvisor/GuidedAdvisorDrawer"));
const ForcePasswordChange = lazy(() => import("./components/ForcePasswordChange"));
const AppTour = lazy(() => import("./components/AppTour"));

// Helper function to get the saved screen from localStorage
const getSavedScreen = () => {
  return localStorage.getItem('currentScreen') || 'landing';
};

// Helper function to save the current screen to localStorage
const saveCurrentScreen = (screenName) => {
  localStorage.setItem('currentScreen', screenName);
};

// Helper function to clear the saved screen
const clearSavedScreen = () => {
  localStorage.removeItem('currentScreen');
};

function App() {
  const [outputportfolioscreen, setoutputportfolioscreen] = useState(false);
  const [riskscreen, setriskscreen] = useState(false);
  const [landingscreen, setlandingscreen] = useState(true);
  const [loginScreen, setLoginScreen] = useState(false);
  const [mainscreen, setMainscreen] = useState(false);
  const [monitorscreen, setmonitorscreen] = useState(false);
  const [advisorycreen, setadvisorycreen] = useState(false);
  const [calulateriskmainscreen, setcalulateriskmainscreen] = useState(false);
  const [adminScreen, setadminScreen] = useState(false);
  const [enterprise, setEnterprise] = useState("");
  const isSuperAdmin = useSelector((state) => state.isSuperAdmin);
  const modulePermissions = useSelector((state) => state.modulePermissions);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [numberOfAttempts, setNumberOfAttempts] = useState(0);
  const [showGuidedAdvisor, setShowGuidedAdvisor] = useState(false);
  const [tourPage, setTourPage] = useState(null);
  const [tourKey, setTourKey] = useState(0);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [forceChangeUser, setForceChangeUser] = useState(null);
  const dispatch = useDispatch();

  // Helper function to reset all screens to false
  const resetAllScreens = () => {
    setlandingscreen(false);
    setLoginScreen(false);
    setoutputportfolioscreen(false);
    setriskscreen(false);
    setmonitorscreen(false);
    setadvisorycreen(false);
    setcalulateriskmainscreen(false);
    setadminScreen(false);
  };

  // Helper function to restore screen from localStorage
  const restoreScreen = (screenName) => {
    resetAllScreens();
    switch (screenName) {
      case 'dashboard':
        setoutputportfolioscreen(true);
        break;
      case 'monitor':
        setmonitorscreen(true);
        break;
      case 'cropSuitability':
        setcalulateriskmainscreen(true);
        break;
      case 'risk':
        setriskscreen(true);
        break;
      case 'advisory':
        setadvisorycreen(true);
        break;
      case 'admin':
      case 'adminProfile':
      case 'adminSettings':
      case 'addUserRole':
      case 'addUserGroup':
      case 'passwordSettings':
      case 'enterpriseManagement':
      case 'enterpriseDetail':
        setadminScreen(true);
        break;
      case 'landing':
      default:
        setlandingscreen(true);
        break;
    }
  };


  // Check if user is already authenticated on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const jwtToken = session.getAccessToken().getJwtToken();
        // Try to get enterprise from localStorage or other persistent storage
        const savedEnterprise = localStorage.getItem('enterprise');
        if (savedEnterprise) {
          setEnterprise(savedEnterprise);
          dispatch(changeEnterprise(savedEnterprise));
        }
        const savedDisplayName = localStorage.getItem('enterpriseDisplayName');
        if (savedDisplayName) {
          dispatch(changeEnterpriseDisplayName(savedDisplayName));
        }
        const savedSuperAdmin = localStorage.getItem('isSuperAdmin');
        if (savedSuperAdmin) {
          dispatch(changeIsSuperAdmin(JSON.parse(savedSuperAdmin)));
        }
        // If the user still needs to change their password, show the gate
        if (localStorage.getItem('must_change_password') === 'true') {
          setForceChangeUser(currentUser);
          setShowForcePasswordChange(true);
          setlandingscreen(false);
          setLoginScreen(false);
          const savedEmail = localStorage.getItem('loginEmail');
          if (savedEmail) setUsername(savedEmail);
          return;
        }

        setIsAuthenticated(true);
        setUser(currentUser);

        // Hydrate module permissions from localStorage immediately
        const savedPermissions = localStorage.getItem('modulePermissions');
        if (savedPermissions) {
          try {
            dispatch(changeModulePermissions(JSON.parse(savedPermissions)));
          } catch (e) { /* ignore parse errors */ }
        }

        // Always verify super-admin status with the server on session restore
        const loginEmail = localStorage.getItem('loginEmail') || currentUser?.attributes?.email || currentUser?.username;
        if (loginEmail) {
          localStorage.setItem('loginEmail', loginEmail);
          dispatch(changeLoginEmail(loginEmail));
          checkSuperAdminStatus(loginEmail);
          const savedEnt = localStorage.getItem('enterprise');
          if (savedEnt) fetchModulePermissions(loginEmail, savedEnt);
        }

        // Restore the saved screen after successful authentication
        const savedScreen = getSavedScreen();
        if (savedScreen && savedScreen !== 'landing') {
          restoreScreen(savedScreen);
        } else {
          // Default to dashboard if no saved screen
          restoreScreen('dashboard');
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.setItem('isAuthenticated', 'false');
        // Show landing page for unauthenticated users
        restoreScreen('landing');
      }
    };

    checkAuth();
    // eslint-disable-next-line
  }, [dispatch]);

  // Save the current screen to localStorage whenever it changes
  useEffect(() => {
    if (!isAuthenticated) return; // Only save screens for authenticated users

    if (outputportfolioscreen) saveCurrentScreen('dashboard');
    else if (monitorscreen) saveCurrentScreen('monitor');
    else if (calulateriskmainscreen) saveCurrentScreen('cropSuitability');
    else if (riskscreen) saveCurrentScreen('risk');
    else if (advisorycreen) saveCurrentScreen('advisory');
    else if (adminScreen) saveCurrentScreen('admin');
  }, [
    isAuthenticated,
    outputportfolioscreen,
    monitorscreen,
    calulateriskmainscreen,
    riskscreen,
    advisorycreen,
    adminScreen,
  ]);

  // Determine the active page name for tour purposes
  const getActivePageName = () => {
    if (outputportfolioscreen) return 'dashboard';
    if (calulateriskmainscreen) return 'cropSuitability';
    if (monitorscreen) return 'monitor';
    if (advisorycreen) return 'advisory';
    return null;
  };

  // Auto-start dashboard tour once on first-ever login
  const hasAutoStartedTour = useRef(false);
  useEffect(() => {
    if (
      isAuthenticated && outputportfolioscreen &&
      !hasAutoStartedTour.current &&
      !localStorage.getItem('tourCompleted_dashboard') &&
      !localStorage.getItem('tourDismissed_dashboard')
    ) {
      hasAutoStartedTour.current = true;
      setTourPage('dashboard');
      setTourKey((k) => k + 1);
    }
    // eslint-disable-next-line
  }, [isAuthenticated, outputportfolioscreen]);

  // Stop any running tour when the user navigates away from that page
  useEffect(() => {
    if (!isAuthenticated) return;
    const page = getActivePageName();
    setTourPage((prev) => {
      if (prev && prev !== page) return null;
      return prev;
    });
    // eslint-disable-next-line
  }, [isAuthenticated, outputportfolioscreen, calulateriskmainscreen, monitorscreen, advisorycreen]);

  const handleTourFinish = () => {
    if (tourPage) {
      localStorage.setItem(`tourCompleted_${tourPage}`, 'true');
    }
    setTourPage(null);
  };

  const startTourForCurrentPage = () => {
    const page = getActivePageName();
    if (page) {
      setTourPage(page);
      setTourKey((k) => k + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const MAX_ATTEMPTS = 3;
    const normalizedEmail = username.trim().toLowerCase();

    try {
      // Step 1: Check for enterprise name
      const enterpriseResult = await fetch("/api/admin/getEnterprise", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      if (!enterpriseResult.ok) {
        const data = await enterpriseResult.json();
        throw new Error(data.error || "No enterprise found for the given user.");
      }

      const enterpriseData = await enterpriseResult.json();
      const enterprise_name = enterpriseData.enterprise;
      const enterprise_display = enterpriseData.display_name || enterprise_name;
      const user_display_name = enterpriseData.user_name || '';

      // Step 2: Set enterprise state and dispatch actions
      setEnterprise(enterprise_name);
      dispatch(changeEnterprise(enterprise_name));
      dispatch(changeEnterpriseDisplayName(enterprise_display));
      localStorage.setItem('enterprise', enterprise_name);
      localStorage.setItem('enterpriseDisplayName', enterprise_display);
      if (user_display_name) {
        localStorage.setItem('userDisplayName', user_display_name);
      }

      // Step 3: Check for account lock
      const lockCheck = await fetch("/api/admin/checkLock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail, enterprise: enterprise_name }),
      });

      const lockData = await lockCheck.json();
      if (!lockCheck.ok) {
        localStorage.removeItem('enterprise');
        throw new Error(lockData.error || "Account locked. Try again later.");
      }

      // Step 4: Sign in
      const user = await Auth.signIn(normalizedEmail, password);

      // Step 5: Get JWT token and reset lock
      const session = await Auth.currentSession();
      const jwtToken = session.getAccessToken().getJwtToken();

      await fetch("/api/admin/resetLock", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: jwtToken },
        body: JSON.stringify({ email: normalizedEmail, enterprise: enterprise_name, admin_email: normalizedEmail }),
      });

      // Reset attempts on successful login
      setNumberOfAttempts(0);

      if (user.must_change_password) {
        localStorage.setItem('must_change_password', 'true');
        localStorage.setItem('loginEmail', normalizedEmail);
        setForceChangeUser(user);
        setShowForcePasswordChange(true);
        return;
      }

      localStorage.removeItem('must_change_password');
      onSuccess(user);

    } catch (err) {
      setError(err.message);

      // Increment attempts and lock account if limit is reached
      setNumberOfAttempts(prevAttempts => {
        const newAttempts = prevAttempts + 1;
        if (newAttempts >= MAX_ATTEMPTS) {
          // Ensure a valid enterprise is available before attempting to lock
          const currentEnterprise = localStorage.getItem('enterprise') || enterprise;
          if (currentEnterprise) {
            fetch("/api/admin/incrementLock", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: username, enterprise: currentEnterprise }),
            }).catch(lockError => {
              console.error("Failed to increment lock:", lockError);
            });
          }
        }
        return newAttempts;
      });

    } finally {
      setLoading(false);
    }
  };


  const checkSuperAdminStatus = async (emailOverride) => {
    try {
      const email = emailOverride || username || localStorage.getItem('loginEmail');
      if (!email) return;
      const { accessToken: { jwtToken } } = await Auth.currentSession();
      const response = await fetch("/api/admin/checkSuperAdmin", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: jwtToken },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        const data = await response.json();
        dispatch(changeIsSuperAdmin(data.is_super_admin));
        localStorage.setItem('isSuperAdmin', JSON.stringify(data.is_super_admin));
      }
    } catch (error) {
      console.error("Error checking super admin status:", error);
    }
  };

  const fetchModulePermissions = async (email, enterpriseName) => {
    try {
      const { accessToken: { jwtToken } } = await Auth.currentSession();
      const response = await fetch("/api/admin/getUserRolePermission", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: jwtToken },
        body: JSON.stringify({ email, enterprise: enterpriseName }),
      });
      if (response.ok) {
        const data = await response.json();
        const perms = (data && data[0]) || {};
        dispatch(changeModulePermissions(perms));
        localStorage.setItem('modulePermissions', JSON.stringify(perms));
      } else {
        const err = await response.json().catch(() => ({}));
        console.error("Failed to fetch module permissions:", response.status, err.error || err);
      }
    } catch (error) {
      console.error("Error fetching module permissions:", error);
    }
  };

  const onSuccess = async (userData) => {
    const normalizedEmail = username.trim().toLowerCase();
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('loginEmail', normalizedEmail);
    dispatch(changeLoginEmail(normalizedEmail));
    saveCurrentScreen('dashboard');
    setLoginScreen(false);
    setlandingscreen(false);
    setoutputportfolioscreen(true);
    const currentEnterprise = localStorage.getItem('enterprise') || enterprise;
    await Promise.all([
      checkSuperAdminStatus(),
      fetchModulePermissions(normalizedEmail, currentEnterprise),
    ]);
  };


  const handleForcePasswordComplete = () => {
    localStorage.removeItem('must_change_password');
    setShowForcePasswordChange(false);
    if (forceChangeUser) onSuccess(forceChangeUser);
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      // Clear localStorage on logout
      localStorage.removeItem('enterprise');
      localStorage.removeItem('isSuperAdmin');
      localStorage.removeItem('loginEmail');
      localStorage.removeItem('modulePermissions');
      localStorage.removeItem('must_change_password');
      localStorage.removeItem('tourCompleted_dashboard');
      localStorage.removeItem('tourCompleted_cropSuitability');
      localStorage.removeItem('tourCompleted_monitor');
      localStorage.removeItem('tourCompleted_advisory');
      clearSavedScreen();
      setIsAuthenticated(false);
      localStorage.setItem('isAuthenticated', 'false');
      window.location.reload();
    } catch (error) {
      console.log("error signing out: ", error);
    }
  };

  // Clears and restarts the inactivity timer
  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      // Auto logout after 60 min of no activity
      await Auth.signOut();
      localStorage.removeItem('enterprise');
      localStorage.removeItem('modulePermissions');
      clearSavedScreen();
      localStorage.setItem('isAuthenticated', 'false');
      handleLogout();
    }, 60 * 60 * 1000); // 60 minutes in milliseconds
  };

  useEffect(() => {
    // Events that count as user activity
    const events = ["mousemove", "keydown", "click", "scroll"];

    // Attach listeners
    events.forEach((event) => window.addEventListener(event, resetTimer));

    // Start timer initially
    resetTimer();

    return () => {
      // Cleanup listeners + timers when component unmounts
      events.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // Redirect to first available screen if current screen is not permitted
  useEffect(() => {
    if (!isAuthenticated || modulePermissions === null) return;
    // Determine current screen name
    let currentScreen = null;
    if (outputportfolioscreen) currentScreen = 'dashboard';
    else if (calulateriskmainscreen) currentScreen = 'cropSuitability';
    else if (riskscreen) currentScreen = 'risk';
    else if (monitorscreen) currentScreen = 'monitor';
    else if (advisorycreen) currentScreen = 'advisory';
    else if (adminScreen) {
      // Gate admin screen by Admin module permission when available
      if (modulePermissions && "Admin" in modulePermissions && !canRead(modulePermissions, 'Admin')) {
        const fallback = getFirstAvailableScreen(modulePermissions);
        if (fallback) {
          restoreScreen(fallback);
          saveCurrentScreen(fallback);
        }
      }
      return;
    }

    if (!currentScreen) return;
    const mod = SCREEN_TO_MODULE[currentScreen];
    if (mod && !canRead(modulePermissions, mod)) {
      const fallback = getFirstAvailableScreen(modulePermissions);
      if (fallback) {
        restoreScreen(fallback);
        saveCurrentScreen(fallback);
      }
    }
    // eslint-disable-next-line
  }, [modulePermissions, isAuthenticated, outputportfolioscreen, calulateriskmainscreen, riskscreen, monitorscreen, advisorycreen, adminScreen]);

  const handleAccess = () => {
    if (isAuthenticated) {
      setoutputportfolioscreen(true);
      setlandingscreen(false);
      setLoginScreen(false);
      return;
    }
    setLoginScreen(true);
    setlandingscreen(false);
  };

  const handleBackToHome = () => {
    setLoginScreen(false);
    setlandingscreen(true);
  };

  return (

    <>
      <Suspense
        fallback={
          <div className="dashboard-shell" role="status" aria-live="polite">
            <div className="dashboard-glow" />
            <div className="dashboard-layout">
              {/* Sidebar Skeleton */}
              <aside className="dashboard-sidebar">
                <div className="dashboard-sidebar-header">
                  <div className="app-skeleton" style={{ width: '36px', height: '36px', borderRadius: '8px' }} />
                  <div className="app-skeleton" style={{ width: '80px', height: '20px', borderRadius: '4px' }} />
                </div>
                <nav className="dashboard-nav">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="app-skeleton" style={{ width: '100%', height: '44px', borderRadius: '14px', marginBottom: '8px' }} />
                  ))}
                </nav>
              </aside>

              {/* Main Content Skeleton */}
              <main className="dashboard-main">
                <div className="dashboard-page-skeleton">
                  {/* Header Skeleton */}
                  <div className="dashboard-skeleton-header">
                    <div className="dashboard-skeleton-header-left">
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: '140px' }} />
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: '200px', height: '28px' }} />
                    </div>
                    <div className="dashboard-skeleton-header-right">
                      <div className="dashboard-skeleton dashboard-skeleton-btn" />
                      <div className="dashboard-skeleton dashboard-skeleton-btn lg" />
                    </div>
                  </div>

                  {/* Stats Skeleton */}
                  <div className="dashboard-skeleton-stats">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="dashboard-skeleton-stat-card">
                        <div className="dashboard-skeleton dashboard-skeleton-stat-label" />
                        <div className="dashboard-skeleton dashboard-skeleton-stat-value" />
                        <div className="dashboard-skeleton dashboard-skeleton-stat-delta" />
                      </div>
                    ))}
                  </div>

                  {/* Main Grid Skeleton */}
                  <div className="dashboard-skeleton-main-grid">
                    <div className="dashboard-skeleton-map-card">
                      <div className="dashboard-skeleton-map-header">
                        <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: '160px', height: '20px' }} />
                        <div className="dashboard-skeleton-map-controls">
                          <div className="dashboard-skeleton dashboard-skeleton-select" />
                          <div className="dashboard-skeleton dashboard-skeleton-select" />
                        </div>
                      </div>
                      <div className="dashboard-skeleton dashboard-skeleton-map-content" />
                    </div>

                    <div className="dashboard-skeleton-sidebar-card">
                      <div className="dashboard-skeleton dashboard-skeleton-sidebar-title" />
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="dashboard-skeleton-sidebar-item">
                          <div className="dashboard-skeleton dashboard-skeleton-rank" />
                          <div className="dashboard-skeleton-region-info">
                            <div className="dashboard-skeleton dashboard-skeleton-region-name" />
                            <div className="dashboard-skeleton dashboard-skeleton-region-score" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Yield Section Skeleton */}
                  <div className="dashboard-skeleton-yield">
                    <div className="dashboard-skeleton-yield-header">
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: '180px', height: '20px' }} />
                    </div>
                    <div className="dashboard-skeleton dashboard-skeleton-yield-chart" />
                  </div>
                </div>
              </main>
            </div>
          </div>
        }
      >
        {landingscreen && (
          <LandingPage
            onAccess={handleAccess}
          />
        )}
        {!landingscreen && !isAuthenticated && loginScreen ? (
          <div className="auth-shell">
            {/* Left branding panel */}
            <div className="auth-brand-panel">
              <div className="auth-brand-content">
                <button type="button" className="auth-brand-logo" onClick={handleBackToHome}>
                  <img src="https://acreafrica.com/wp-content/uploads/2024/04/cropped-acre-africa-emblem-192x192.png" alt="ACRE Africa logo" />
                  <span>TARA</span>
                </button>
                <h1 className="auth-brand-heading">Agricultural Intelligence for Smarter Decisions</h1>
                <p className="auth-brand-desc">
                  Monitor crop performance, assess climate risks, and make data-driven lending decisions — all in one platform.
                </p>
                <div className="auth-brand-features">
                  <div className="auth-brand-feature">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <span>Real-time crop monitoring</span>
                  </div>
                  <div className="auth-brand-feature">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <span>Climate risk assessment</span>
                  </div>
                  <div className="auth-brand-feature">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <span>Crop suitability analysis</span>
                  </div>
                </div>
              </div>
              <footer className="auth-brand-footer">
                © 2026 TARA Intelligence. Powered by ACRE Africa.
              </footer>
            </div>

            {/* Right form panel */}
            <div className="auth-form-panel">
              <div className="auth-card">
                <h2>Welcome back</h2>
                <p className="auth-subtitle">Enter your details to access your dashboard.</p>

                <form onSubmit={handleSubmit}>
                  <div className="auth-field">
                    <label htmlFor="username">Email Address</label>
                    <input
                      id="username"
                      name="username"
                      type="email"
                      placeholder="name@company.com"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>

                  <div className="auth-field">
                    <label htmlFor="password">Password</label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button type="button" className="auth-link">Forgot password?</button>
                  </div>

                  {error && <p className="auth-error">{error}</p>}

                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                </form>

                <p className="auth-footer">
                  Need access? <span>Contact administrator</span>
                </p>
              </div>
            </div>
          </div>) : null}
        {!landingscreen && isAuthenticated ? (
          <div className="App">
            {outputportfolioscreen && canRead(modulePermissions, 'Home') && (
              <OutputPortfolio
                mainscreen={mainscreen}
                setMainscreen={setMainscreen}
                setoutputportfolioscreen={setoutputportfolioscreen}
                setcalulateriskmainscreen={setcalulateriskmainscreen}
                calulateriskmainscreen={calulateriskmainscreen}
                setmonitorscreen={setmonitorscreen}
                advisorycreen={advisorycreen}
                setadvisorycreen={setadvisorycreen}
                adminProfilescreen={adminScreen}
                setadminProfilescreen={setadminScreen}
                onOpenGuidedAdvisor={() => setShowGuidedAdvisor(true)}
                onStartTour={startTourForCurrentPage}
              />
            )}
            {monitorscreen && canRead(modulePermissions, 'Monitor') && (
              <Monitor
                setmonitorscreen={setmonitorscreen}
                setMainscreen={setMainscreen}
                riskscreen={riskscreen}
                setriskscreen={setriskscreen}
                setcalulateriskmainscreen={setcalulateriskmainscreen}
                setoutputportfolioscreen={setoutputportfolioscreen}
                advisorycreen={advisorycreen}
                setadvisorycreen={setadvisorycreen}
                adminProfilescreen={adminScreen}
                setadminProfilescreen={setadminScreen}
                onOpenGuidedAdvisor={() => setShowGuidedAdvisor(true)}
                onStartTour={startTourForCurrentPage}
              />
            )}

            {calulateriskmainscreen && canRead(modulePermissions, 'Calculate') && (
              <CalculateRiskMain
                setmonitorscreen={setmonitorscreen}
                setcalulateriskmainscreen={setcalulateriskmainscreen}
                setMainscreen={setMainscreen}
                riskscreen={riskscreen}
                setriskscreen={setriskscreen}
                setoutputportfolioscreen={setoutputportfolioscreen}
                advisorycreen={advisorycreen}
                setadvisorycreen={setadvisorycreen}
                adminProfilescreen={adminScreen}
                setadminProfilescreen={setadminScreen}
                onOpenGuidedAdvisor={() => setShowGuidedAdvisor(true)}
                onStartTour={startTourForCurrentPage}
              />
            )}
            {riskscreen && canRead(modulePermissions, 'Calculate') && (
              <OutputIndividual
                setcalulateriskmainscreen={setcalulateriskmainscreen}
                mainscreen={mainscreen}
                setmonitorscreen={setmonitorscreen}
                setMainscreen={setMainscreen}
                setriskscreen={setriskscreen}
                setoutputportfolioscreen={setoutputportfolioscreen}
                advisorycreen={advisorycreen}
                setadvisorycreen={setadvisorycreen}
                adminProfilescreen={adminScreen}
                setadminProfilescreen={setadminScreen}
              />
            )}
            {advisorycreen && canRead(modulePermissions, 'Advisory') && (
              <Advisory
                setcalulateriskmainscreen={setcalulateriskmainscreen}
                mainscreen={mainscreen}
                setmonitorscreen={setmonitorscreen}
                setMainscreen={setMainscreen}
                setriskscreen={setriskscreen}
                setoutputportfolioscreen={setoutputportfolioscreen}
                advisorycreen={advisorycreen}
                setadvisorycreen={setadvisorycreen}
                adminProfilescreen={adminScreen}
                setadminProfilescreen={setadminScreen}
                onOpenGuidedAdvisor={() => setShowGuidedAdvisor(true)}
                onStartTour={startTourForCurrentPage}
              />
            )}
            {adminScreen && (
              <AdminPage
                onSwitchToUser={() => {
                  resetAllScreens();
                  setoutputportfolioscreen(true);
                }}
              />
            )}
            <GuidedAdvisorDrawer
              isOpen={showGuidedAdvisor}
              onClose={() => setShowGuidedAdvisor(false)}
            />
            <AppTour key={tourKey} run={!!tourPage} page={tourPage || 'dashboard'} onFinish={handleTourFinish} />
          </div>) : null}
        {showForcePasswordChange && (
          <ForcePasswordChange
            username={username.trim().toLowerCase()}
            enterprise={localStorage.getItem('enterprise') || enterprise}
            onComplete={handleForcePasswordComplete}
          />
        )}
      </Suspense>
    </>
  );
}
export default App;

